import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, Calendar as CalIcon, Sparkles, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SectionFlourish, Sprig, HeartDivider, CornerLeaves } from "@/components/Botanical";

const PRODUCTS = [
  { id: "bouquet", label: "Cupcake Bouquet", desc: "Handcrafted floral arrangement wrapped in tissue & box." },
  { id: "box", label: "Cupcakes in a Box", desc: "Boxed floral cupcake presentation — single serving florals." },
];

const BOUQUET_SIZES = [
  { id: "3pc", label: "3 piece", price: "Starting at $15.99" },
  { id: "7pc", label: "7 piece", price: "Starting at $39.99" },
  { id: "12pc", label: "12 piece", price: "Starting at $59.99" },
  { id: "19pc", label: "19 piece", price: "Starting at $89.00" },
];

const BOX_SIZES = [
  { id: "4pc", label: "4 piece", price: "Starting at $19.99" },
  { id: "6pc", label: "6 piece", price: "Starting at $29.99" },
  { id: "12pc", label: "12 piece", price: "Starting at $49.99" },
];

const OCCASIONS = [
  "Birthday", "Wedding", "Bridal Shower", "Baby Shower", "Anniversary",
  "Graduation", "Mother's Day", "Corporate Event", "Holiday Celebration", "Other",
];

const Step = ({ active, done, n, title }) => (
  <div className="flex items-center gap-3">
    <span className={`w-8 h-8 flex items-center justify-center text-xs border rounded-full font-script text-base ${done ? "bg-rose text-ivory border-rose" : active ? "border-rose text-rose bg-ivory" : "border-hairline text-ink-soft bg-ivory"}`}>
      {done ? <Check size={12} /> : n}
    </span>
    <span className={`text-xs uppercase tracking-[0.2em] hidden md:inline ${active || done ? "text-ink" : "text-ink-soft"}`}>{title}</span>
  </div>
);

const MonthCalendar = ({ value, onChange, blockedSet }) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysIn = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(new Date(year, month, d));

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const monthName = view.toLocaleString("default", { month: "long" });

  return (
    <div className="frame-gold p-6 md:p-8 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button type="button" onClick={() => setView(new Date(year, month-1, 1))} className="p-2 hover:text-rose" aria-label="Previous month" data-testid="cal-prev">
          <ChevronLeft size={18} strokeWidth={1.25} />
        </button>
        <p className="font-script text-3xl text-rose leading-none">{monthName}<span className="text-ink-soft font-display text-xl ml-2 align-middle">{year}</span></p>
        <button type="button" onClick={() => setView(new Date(year, month+1, 1))} className="p-2 hover:text-rose" aria-label="Next month" data-testid="cal-next">
          <ChevronRight size={18} strokeWidth={1.25} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {["S","M","T","W","T","F","S"].map((d,i) => <div key={i} className="overline text-[10px] py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dStr = fmt(d);
          const isPast = d < today;
          const isBlocked = blockedSet.has(dStr) || isPast;
          const isSelected = value === dStr;
          return (
            <button
              type="button"
              key={i}
              disabled={isBlocked}
              onClick={() => onChange(dStr)}
              data-testid={`cal-day-${dStr}`}
              aria-disabled={isBlocked}
              className={`aspect-square text-sm flex items-center justify-center transition-colors rounded-full
                ${isSelected ? "bg-rose text-ivory" : ""}
                ${isBlocked ? "text-ink-soft/40 line-through cursor-not-allowed" : "hover:bg-blush text-ink"}
              `}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="hairline-soft my-4" />
      <p className="text-xs text-ink-soft font-light text-center">Crossed-out dates are unavailable.</p>
    </div>
  );
};

export default function Inquiry() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    product: "", size: "",
    cake_flavor: "", filling_flavors: [],
    gift_box: "none",
    selected_add_ons: [],
    custom_colors: "",
    occasion: "", event_date: "",
    inspiration_urls: ["", "", ""],
    delivery_type: "pickup", delivery_address: "",
    customer_name: "", customer_email: "", customer_phone: "",
    notes: "",
  });
  const [addOns, setAddOns] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const [s, b, a] = await Promise.all([
        api.get("/settings"),
        api.get("/blocked-dates"),
        api.get("/add-ons"),
      ]);
      setSettings(s.data);
      setBlocked(b.data);
      setAddOns(a.data);
    };
    fetchData();
  }, []);

  const blockedSet = useMemo(() => new Set(blocked.map(b => b.date)), [blocked]);
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleFilling = (f) => setField("filling_flavors", form.filling_flavors.includes(f) ? form.filling_flavors.filter(x => x !== f) : [...form.filling_flavors, f]);
  const toggleAddOn = (name) => setField("selected_add_ons", form.selected_add_ons.includes(name) ? form.selected_add_ons.filter(x => x !== name) : [...form.selected_add_ons, name]);
  const setInspiration = (i, v) => {
    const next = [...form.inspiration_urls]; next[i] = v; setField("inspiration_urls", next);
  };

  const CAKE_FLAVORS = settings?.cake_flavors || ["White", "Yellow", "Chocolate", "Lemon"];
  const PREMIUM_FLAVORS = settings?.premium_flavors || ["Vanilla Bean", "Carrot", "Oreo Cookie"];
  const FILLING_FLAVORS = settings?.filling_flavors || ["Strawberry", "Raspberry", "Blueberry", "Caramel", "Cream Cheese", "Lemon Curd"];

  const STEPS = ["Product", "Size", "Flavors", "Colors", "Date", "Details"];
  const canNext = () => {
    switch (step) {
      case 0: return !!form.product;
      case 1: return !!form.size;
      case 2: return !!form.cake_flavor;
      case 3: return !!form.occasion;
      case 4: return !!form.event_date;
      default: return true;
    }
  };

  const sizesForProduct = form.product === "box" ? BOX_SIZES : BOUQUET_SIZES;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Build flavor display string for legacy field
      const flavorList = [];
      if (form.cake_flavor) flavorList.push(`Cake: ${form.cake_flavor}`);
      if (form.filling_flavors.length) flavorList.push(`Filling: ${form.filling_flavors.join(", ")}`);
      await api.post("/inquiries", {
        ...form,
        flavors: flavorList,
        color_palette: "custom",
        inspiration_urls: form.inspiration_urls.filter(u => u.trim()),
      });
      setSubmitted(true);
    } catch (err) {
      alert("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-watercolor min-h-screen">
        <Navbar logoUrl={settings?.logo_url} />
        <div className="min-h-screen flex items-center justify-center px-6 pt-32 pb-20 relative">
          <CornerLeaves className="absolute top-24 left-4 md:left-12 w-32 md:w-44 opacity-60" />
          <CornerLeaves className="absolute bottom-12 right-4 md:right-12 w-32 md:w-44 opacity-60" flip />
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl text-center frame-gold p-10 md:p-14 bg-ivory"
            data-testid="inquiry-thankyou"
          >
            <div className="w-14 h-14 bg-rose text-ivory flex items-center justify-center mx-auto mb-8 rounded-full">
              <Check size={24} strokeWidth={1.25} />
            </div>
            <p className="overline text-rose mb-3">Thank You</p>
            <p className="font-pinyon text-5xl md:text-6xl text-rose leading-none">Your inquiry has bloomed.</p>
            <HeartDivider className="w-40 h-5 mx-auto my-8" />
            <p className="text-base md:text-lg text-ink-soft font-light leading-relaxed">
              We've received your request and will be in touch within 24 hours to confirm availability and final pricing. Every Tammy's Treats creation begins with a conversation — we can't wait to begin yours.
            </p>
            {settings?.venmo_enabled && settings?.venmo_handle && (
              <div className="mt-10 pt-8 border-t border-hairline">
                <p className="overline text-rose mb-3">When Your Order Is Confirmed</p>
                <p className="text-sm text-ink-soft font-light mb-5">Payment is collected via Venmo after we confirm your details.</p>
                <a
                  href={settings.venmo_url || `https://venmo.com/u/${settings.venmo_handle.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="thankyou-venmo"
                  className="btn-pill-pink"
                  style={{ background: "#3D95CE", borderColor: "#3D95CE" }}
                >
                  <Wallet size={14} strokeWidth={1.5} /> Pay via Venmo · {settings.venmo_handle}
                </a>
              </div>
            )}
            <button onClick={() => navigate("/")} className="btn-pill-pink mt-10" data-testid="thankyou-home-btn">
              Return Home
            </button>
          </motion.div>
        </div>
        <Footer settings={settings} />
      </div>
    );
  }

  return (
    <div className="bg-watercolor-soft min-h-screen">
      <Navbar logoUrl={settings?.logo_url} />

      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-20 relative">
        <CornerLeaves className="absolute top-28 left-2 w-32 opacity-40 hidden md:block" />
        <CornerLeaves className="absolute top-28 right-2 w-32 opacity-40 hidden md:block" flip />

        <div className="max-w-[1100px] mx-auto relative">
          <div className="text-center mb-12">
            <p className="overline text-rose mb-4">The Inquiry</p>
            <p className="font-pinyon text-5xl md:text-7xl text-rose leading-[0.85]">Begin Your Bouquet</p>
            <SectionFlourish className="w-48 h-6 mx-auto mt-6" />
            <p className="mt-6 text-ink-soft font-light max-w-xl mx-auto">We will contact you to confirm availability and final pricing.</p>
            <a href="/menu" data-testid="inquiry-view-menu" className="elegant-link text-xs uppercase tracking-[0.22em] text-ink mt-6 inline-block">
              View the full menu →
            </a>
          </div>

          {/* Stepper */}
          <div className="flex items-center justify-between gap-2 md:gap-4 border-y border-hairline py-6 mb-12 overflow-x-auto bg-ivory/60 backdrop-blur px-4">
            {STEPS.map((t, i) => (
              <Step key={i} active={step === i} done={step > i} n={i + 1} title={t} />
            ))}
          </div>

          <div className="frame-gold p-8 md:p-14 bg-ivory">
            <form onSubmit={submit}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5 }}
                  className="min-h-[420px]"
                >
                  {/* STEP 0 - Product */}
                  {step === 0 && (
                    <div data-testid="step-product">
                      <p className="font-script text-4xl text-rose text-center leading-none mb-2">Step One</p>
                      <h2 className="font-display text-3xl text-ink text-center mb-8">Choose Your Product</h2>
                      <div className="grid md:grid-cols-2 gap-5">
                        {PRODUCTS.map(p => (
                          <button
                            type="button" key={p.id}
                            data-testid={`product-${p.id}`}
                            onClick={() => setField("product", p.id)}
                            className={`text-center p-8 border-2 transition-all rounded-sm ${form.product === p.id ? "border-rose bg-blush" : "border-hairline hover:border-rose/60 bg-ivory"}`}
                          >
                            <p className="font-script text-3xl text-rose leading-none mb-3">{p.label}</p>
                            <Sprig className="w-12 h-4 mx-auto my-3 opacity-70" />
                            <p className="text-sm text-ink-soft mt-2 font-light">{p.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* STEP 1 - Size */}
                  {step === 1 && (
                    <div data-testid="step-size">
                      <p className="font-script text-4xl text-rose text-center leading-none mb-2">Step Two</p>
                      <h2 className="font-display text-3xl text-ink text-center mb-3">Choose a Size</h2>
                      <p className="text-sm text-ink-soft text-center mb-8 font-light">
                        {form.product === "box" ? "Cupcakes in a Box" : "Cupcake Bouquet"} — final pricing confirmed after review.
                      </p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {sizesForProduct.map(s => (
                          <button
                            type="button" key={s.id}
                            data-testid={`size-${s.id}`}
                            onClick={() => setField("size", s.id)}
                            className={`text-center p-6 border-2 transition-all ${form.size === s.id ? "border-rose bg-blush" : "border-hairline hover:border-rose/60 bg-ivory"}`}
                          >
                            <p className="font-display text-2xl text-ink">{s.label}</p>
                            <div className="hairline w-12 mx-auto my-3" />
                            <p className="text-xs text-ink-soft uppercase tracking-wider">{s.price}</p>
                          </button>
                        ))}
                      </div>

                      {/* Add-ons multi-select */}
                      {addOns.length > 0 && (
                        <div className="mt-12 max-w-3xl mx-auto">
                          <p className="overline text-rose mb-1 text-center">Add-ons <span className="text-ink-soft normal-case tracking-normal text-[10px]">(optional · pick any)</span></p>
                          <p className="text-xs text-ink-soft text-center mb-5 font-light italic">Extra touches — final pricing confirmed on our call.</p>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {addOns.map(a => {
                              const selected = form.selected_add_ons.includes(a.name);
                              return (
                                <button
                                  type="button"
                                  key={a.id}
                                  data-testid={`addon-${a.id}`}
                                  onClick={() => toggleAddOn(a.name)}
                                  className={`text-center p-5 border-2 transition-all ${selected ? "border-rose bg-blush" : "border-hairline hover:border-rose/60 bg-ivory"}`}
                                >
                                  <p className="font-script text-2xl text-rose leading-none">{a.name}</p>
                                  <p className="text-[11px] text-ink-soft mt-2 font-light leading-relaxed">{a.description}</p>
                                  {a.price && <p className="overline text-gold mt-3">{a.price}</p>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* STEP 2 - Flavors */}
                  {step === 2 && (
                    <div data-testid="step-flavors">
                      <p className="font-script text-4xl text-rose text-center leading-none mb-2">Step Three</p>
                      <h2 className="font-display text-3xl text-ink text-center mb-3">Choose Your Flavors</h2>
                      <p className="text-sm text-ink-soft text-center mb-10 font-light">Select one cake flavor and any fillings you'd love.</p>

                      <div className="grid lg:grid-cols-2 gap-10">
                        <div>
                          <p className="overline text-rose mb-4 text-center">Cake Flavor</p>
                          <div className="grid grid-cols-2 gap-3">
                            {CAKE_FLAVORS.map(f => (
                              <button
                                type="button" key={f}
                                data-testid={`cake-${f.toLowerCase()}`}
                                onClick={() => setField("cake_flavor", f)}
                                className={`p-4 border-2 text-sm transition-all ${form.cake_flavor === f ? "border-rose bg-rose text-ivory" : "border-hairline hover:border-rose"}`}
                              >{f}</button>
                            ))}
                          </div>
                          <p className="overline text-gold mt-8 mb-4 text-center">Premium · +$2 per 6pc</p>
                          <div className="grid grid-cols-2 gap-3">
                            {PREMIUM_FLAVORS.map(f => (
                              <button
                                type="button" key={f}
                                data-testid={`premium-${f.toLowerCase().replace(/ /g,'-')}`}
                                onClick={() => setField("cake_flavor", f)}
                                className={`p-4 border-2 text-sm transition-all ${form.cake_flavor === f ? "border-gold bg-gold text-ivory" : "border-gold/40 hover:border-gold"}`}
                              >{f}</button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="overline text-rose mb-4 text-center">Filling Flavors</p>
                          <p className="text-xs text-ink-soft text-center mb-4 italic">Choose one or more — we'll discuss combinations.</p>
                          <div className="grid grid-cols-2 gap-3">
                            {FILLING_FLAVORS.map(f => (
                              <button
                                type="button" key={f}
                                data-testid={`filling-${f.toLowerCase().replace(/ /g,'-')}`}
                                onClick={() => toggleFilling(f)}
                                className={`p-4 border-2 text-sm transition-all ${form.filling_flavors.includes(f) ? "border-rose bg-rose text-ivory" : "border-hairline hover:border-rose"}`}
                              >{f}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 3 - Colors & Occasion */}
                  {step === 3 && (
                    <div data-testid="step-colors">
                      <p className="font-script text-4xl text-rose text-center leading-none mb-2">Step Four</p>
                      <h2 className="font-display text-3xl text-ink text-center mb-3">The Occasion & Colors</h2>
                      <p className="text-sm text-ink-soft text-center mb-10 font-light">Tell us the celebration — and describe your color vision in your own words.</p>

                      <p className="overline text-rose mb-4 text-center">Occasion</p>
                      <div className="flex flex-wrap gap-2 justify-center mb-12">
                        {OCCASIONS.map(o => (
                          <button
                            type="button" key={o}
                            data-testid={`occasion-${o.toLowerCase().replace(/[^a-z]/g, '-')}`}
                            onClick={() => setField("occasion", o)}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all ${form.occasion === o ? "border-rose bg-rose text-ivory" : "border-hairline hover:border-rose"}`}
                          >{o}</button>
                        ))}
                      </div>

                      <p className="overline text-rose mb-4 text-center">Your Desired Colors</p>
                      <div className="max-w-2xl mx-auto">
                        <textarea
                          rows={4}
                          className="elegant-input resize-none"
                          placeholder="Describe your dream palette — e.g., soft blush with sage green and ivory; or burgundy with gold accents; or whatever feels right…"
                          value={form.custom_colors}
                          onChange={e => setField("custom_colors", e.target.value)}
                          data-testid="custom-colors-input"
                        />
                      </div>

                      <div className="mt-12 max-w-2xl mx-auto">
                        <p className="overline text-rose mb-4 text-center">Inspiration Photos <span className="text-ink-soft normal-case tracking-normal text-[10px]">(optional)</span></p>
                        <p className="text-xs text-ink-soft text-center mb-5 font-light italic">Paste up to 3 image links — Pinterest, wedding mood boards, color references.</p>
                        {[0, 1, 2].map(i => (
                          <input
                            key={i}
                            className="elegant-input mb-3"
                            placeholder={`Inspiration link ${i + 1}`}
                            value={form.inspiration_urls[i]}
                            onChange={e => setInspiration(i, e.target.value)}
                            data-testid={`inspiration-${i}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* STEP 4 - Date */}
                  {step === 4 && (
                    <div data-testid="step-date">
                      <p className="font-script text-4xl text-rose text-center leading-none mb-2">Step Five</p>
                      <h2 className="font-display text-3xl text-ink text-center mb-3 flex items-center justify-center gap-3">
                        <CalIcon size={26} strokeWidth={1.25} className="text-rose" /> Select Your Date
                      </h2>
                      <p className="text-sm text-ink-soft text-center mb-10 font-light">Pick a preferred date. We'll confirm availability on our call.</p>
                      <MonthCalendar
                        value={form.event_date}
                        onChange={(d) => setField("event_date", d)}
                        blockedSet={blockedSet}
                      />
                      {form.event_date && (
                        <p className="mt-8 text-center font-script text-3xl text-rose">{form.event_date}</p>
                      )}
                    </div>
                  )}

                  {/* STEP 5 - Details */}
                  {step === 5 && (
                    <div data-testid="step-details">
                      <p className="font-script text-4xl text-rose text-center leading-none mb-2">Step Six</p>
                      <h2 className="font-display text-3xl text-ink text-center mb-10">Your Details</h2>
                      <div className="grid md:grid-cols-2 gap-x-10 gap-y-6 max-w-2xl mx-auto">
                        <div>
                          <label className="overline">Name *</label>
                          <input name="customer_name" className="elegant-input" required value={form.customer_name} onChange={e => setField("customer_name", e.target.value)} data-testid="customer-name-input" />
                        </div>
                        <div>
                          <label className="overline">Email *</label>
                          <input name="customer_email" type="email" className="elegant-input" required value={form.customer_email} onChange={e => setField("customer_email", e.target.value)} data-testid="customer-email-input" />
                        </div>
                        <div>
                          <label className="overline">Phone</label>
                          <input name="customer_phone" className="elegant-input" value={form.customer_phone} onChange={e => setField("customer_phone", e.target.value)} data-testid="customer-phone-input" />
                        </div>
                        <div>
                          <label className="overline">Delivery or Pickup?</label>
                          <div className="flex gap-3 mt-3">
                            {["pickup", "delivery"].map(t => (
                              <button
                                type="button" key={t}
                                data-testid={`delivery-${t}`}
                                onClick={() => setField("delivery_type", t)}
                                className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border ${form.delivery_type === t ? "border-rose bg-rose text-ivory" : "border-hairline"}`}
                              >{t}</button>
                            ))}
                          </div>
                        </div>
                        {form.delivery_type === "delivery" && (
                          <div className="md:col-span-2">
                            <label className="overline">Delivery Address</label>
                            <input className="elegant-input" value={form.delivery_address} onChange={e => setField("delivery_address", e.target.value)} data-testid="delivery-address" />
                            <p className="mt-3 text-xs text-ink-soft italic" data-testid="delivery-distance-note">
                              A delivery fee applies and depends on distance from Mooresville, NC. We'll confirm the fee when we call.
                            </p>
                          </div>
                        )}
                        <div className="md:col-span-2">
                          <label className="overline">Anything else we should know?</label>
                          <textarea rows={4} className="elegant-input resize-none" value={form.notes} onChange={e => setField("notes", e.target.value)} data-testid="notes-input" />
                        </div>
                      </div>
                      <div className="mt-12 border-t border-hairline pt-8 text-center max-w-xl mx-auto space-y-3">
                        <p className="font-display italic text-base md:text-lg text-ink" data-testid="final-price-note">
                          The final price will be worked out individually with you.
                        </p>
                        <p className="text-xs text-ink-soft font-light">
                          We will contact you to confirm availability, packaging, delivery fee (if applicable), and final pricing before any payment is due.
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav buttons */}
              <div className="mt-12 flex items-center justify-between border-t border-hairline pt-8">
                <button
                  type="button"
                  onClick={() => step > 0 ? setStep(step - 1) : navigate("/")}
                  data-testid="inquiry-back"
                  className="elegant-link text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                >
                  <ChevronLeft size={16} strokeWidth={1.25} /> {step === 0 ? "Cancel" : "Back"}
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => canNext() && setStep(step + 1)}
                    disabled={!canNext()}
                    data-testid="inquiry-next"
                    className={`btn-pill-pink ${!canNext() ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Continue <ChevronRight size={14} strokeWidth={1.5} />
                  </button>
                ) : (
                  <button type="submit" disabled={submitting} data-testid="submit-inquiry-btn" className="btn-pill-pink">
                    <Sparkles size={14} strokeWidth={1.5} /> {submitting ? "Submitting..." : "Submit Inquiry"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer settings={settings} />
    </div>
  );
}
